/**
 * ToothChartSvg — shared arch-position diagram (odontogram) SVG body.
 *
 * This is the SAME graphic used by the order-entry picker in apps/main; it was
 * lifted into the design-system so read-only surfaces in every app (portal +
 * lab app) can render the identical diagram instead of a bare text list of
 * position numbers.
 *
 * The (large, static) path geometry lives in `toothchart-geometry.json` and
 * is rendered here by mapping — this keeps the component itself small and the
 * heavy vector DATA out of a linted source file. `g#Fill` paths carry
 * id=T1..T32 so callers (see ToothChart) can recolor the selected
 * positions by querying the group via the forwarded ref.
 */
import type { RefObject } from "react";
import geometry from "./toothchart-geometry.json";

interface FillPath {
  id: string;
  d: string;
}
interface OutlinePath {
  id: string;
  d: string;
  fill: string;
}

const FILL = geometry.fill as FillPath[];
const OUTLINE = geometry.outline as OutlinePath[];

export function ToothChartSvg({
  svgRef,
  className,
}: {
  svgRef: RefObject<SVGSVGElement | null>;
  className?: string;
}) {
  return (
    <svg
      ref={svgRef}
      className={className ?? "cp-odontogram-svg"}
      viewBox={geometry.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <g id="Teeth_Color2W" clipPath={geometry.clip}>
        <g id="Fill">
          {FILL.map((p) => (
            <path key={p.id} id={p.id} d={p.d} fill="white" />
          ))}
        </g>
        <g id="ToothOutline">
          {OUTLINE.map((p, i) => (
            <path key={p.id ? `${p.id}-${i}` : i} id={p.id || undefined} d={p.d} fill={p.fill} />
          ))}
        </g>
      </g>
      <defs>
        <linearGradient
          id="paint0_linear_1789_63868"
          x1="66.9652"
          y1="793.684"
          x2="1028.97"
          y2="793.684"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#737373" />
          <stop offset="0.131685" stopColor="#737373" />
          <stop offset="0.166992" />
          <stop offset="0.504576" stopColor="#F31260" />
          <stop offset="0.825738" />
          <stop offset="0.876839" stopColor="#737373" />
          <stop offset="1" stopColor="#71717A" />
        </linearGradient>
        <linearGradient
          id="paint1_linear_1789_63868"
          x1="844.593"
          y1="1517.86"
          x2="259.372"
          y2="1517.86"
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset="0.494006" stopColor="#F31260" />
          <stop offset="1" />
        </linearGradient>
        <clipPath id="clip0_1789_63868">
          <rect
            width="1095.37"
            height="1680.59"
            fill="white"
            transform="translate(0.143555 0.241699)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}
